<template>
  <div class="confirm-purchase">
    <transition
      :name="
        transitionDirection === DIRECTIONS.FORWARD
          ? 'fade-out-left'
          : 'fade-out-right'
      "
      mode="out-in"
    >
      <template v-if="hasConfirmed">
        <div key="payment-element" class="confirm-purchase__payment-element">
          <payment-element
            :product-id="product.id"
            :is-subscription="isSubscription"
            :discount-info="discountInfo"
            :intent-will-be-given="stripeRequiresIntentForMount"
            :intent-client-secret="intentClientSecret"
            :intent-type="intentType"
            :has-trial="hasTrial"
            :amount="amountInfo.amount"
            :amount-cents="amountInfo.amountCents"
            :currency="amountInfo.currency"
            :symbol="amountInfo.symbol"
            :is-loading-info="isLoadingTax"
            @error="handlePaymentElementError"
            @back="handleBack"
            @change="handleUserDetailsChange"
          >
            <template v-slot:before-button>
              <tax-info
                v-if="showTaxInfo && !hasTrial"
                class="mb-2"
                :tax-info="taxInfo"
                :is-loading="isLoadingTax"
              />
            </template>
          </payment-element>
        </div>
      </template>

      <template v-else>
        <div key="order-summary" class="confirm-purchase__order-summary">
          <h2 class="mb-2">
            {{ $t("components.checkout.confirmPurchase.orderSummaryTitle") }}
          </h2>
          <list no-padding-x>
            <list-item>
              <template slot="left">
                <div class="confirm-purchase__left">
                  <span>{{
                    $t(
                      "components.checkout.confirmPurchase.orderSummaryProduct"
                    )
                  }}</span>
                </div>
              </template>
              <div class="v-list-item__title pl-2">
                {{ product.title }}
              </div>
            </list-item>
            <list-item :align-start="hasTrial">
              <template slot="left">
                <div class="confirm-purchase__left">
                  <span>{{
                    $t("components.checkout.confirmPurchase.orderSummaryAmount")
                  }}</span>
                </div>
              </template>
              <div class="v-list-item__title pl-2">
                <div v-if="hasTrial" class="mb-1">
                  {{ product.trial_period_days }}
                  {{
                    $t(
                      "components.checkout.confirmPurchase.orderSummaryDayFree"
                    )
                  }}
                </div>
                <div>
                  {{ product.symbol || "$" }}{{ product.price }}
                  {{ product.currency }}
                  <template v-if="product.interval">
                    {{ $t("components.checkout.confirmPurchase.orderPer") }}
                    {{ productInterval(product.interval) }}
                  </template>
                </div>
              </div>
            </list-item>

            <template v-if="discountInfo">
              <list-item>
                <template slot="left">
                  <div class="confirm-purchase__left">
                    <span>{{
                      $t("components.checkout.confirmPurchase.discount")
                    }}</span>
                  </div>
                </template>
                <div class="d-flex align-items-center w-100">
                  <div>
                    <div class="v-list-item__title pl-2">
                      -{{ discountInfo.symbol }}{{ discountInfo.amount }}
                      {{ product.currency }}
                    </div>
                  </div>
                  <v-button
                    class="ml-auto flex-shrink-0"
                    primary
                    small
                    ghost
                    @click="attemptRemoveCoupon"
                  >
                    {{ $t("components.checkout.confirmPurchase.remove") }}
                  </v-button>
                </div>
              </list-item>

              <tax-info
                v-if="showTaxInfo"
                :tax-info="taxInfo"
                :no-coupon-tax-info="noCouponTaxInfo"
                :is-loading="isLoadingTax"
              />

              <list-item>
                <template slot="left">
                  <div class="confirm-purchase__left">
                    <span>{{
                      $t("components.checkout.confirmPurchase.totalDue")
                    }}</span>
                  </div>
                </template>
                <div class="v-list-item__title pl-2">
                  <template v-if="showTaxInfo && taxInfo">
                    {{ taxInfo.symbol }}{{ taxInfo.final_amount }}
                    {{ taxInfo.currency }}
                    <template v-if="product.interval">
                      <template
                        v-if="
                          discountInfo.duration === COUPON_DURATIONS.FOREVER
                        "
                      >
                        {{ $t("components.checkout.confirmPurchase.orderPer") }}
                        {{ productInterval(product.interval) }}
                      </template>
                      <template
                        v-else-if="
                          discountInfo.duration ===
                            COUPON_DURATIONS.REPEATING && noCouponTaxInfo
                        "
                      >
                        {{ $t("components.checkout.confirmPurchase.orderPer") }}
                        {{ productInterval(product.interval) }}
                        for {{ discountInfo.duration_in_months }} months
                        <small class="d-block text-grey-60">
                          {{ $t("components.checkout.confirmPurchase.then") }}
                          {{ noCouponTaxInfo.symbol
                          }}{{ noCouponTaxInfo.final_amount }}
                          {{ noCouponTaxInfo.currency }}
                          {{
                            $t("components.checkout.confirmPurchase.orderPer")
                          }}
                          {{ productInterval(product.interval) }}
                        </small>
                      </template>
                      <template v-else-if="noCouponTaxInfo">
                        {{ $t("components.checkout.confirmPurchase.first") }}
                        {{ productInterval(product.interval) }}
                        <small class="d-block text-grey-60">
                          {{ $t("components.checkout.confirmPurchase.then") }}
                          {{ noCouponTaxInfo.symbol
                          }}{{ noCouponTaxInfo.final_amount }}
                          {{ noCouponTaxInfo.currency }}
                          {{
                            $t("components.checkout.confirmPurchase.orderPer")
                          }}
                          {{ productInterval(product.interval) }}
                        </small>
                      </template>
                    </template>
                  </template>

                  <template v-else>
                    {{ discountInfo.symbol }}{{ discountInfo.final_amount }}
                    {{ product.currency }}
                    <template v-if="product.interval">
                      <template
                        v-if="
                          discountInfo.duration === COUPON_DURATIONS.FOREVER
                        "
                      >
                        {{ $t("components.checkout.confirmPurchase.orderPer") }}
                        {{ productInterval(product.interval) }}
                      </template>
                      <template
                        v-else-if="
                          discountInfo.duration === COUPON_DURATIONS.REPEATING
                        "
                      >
                        {{ $t("components.checkout.confirmPurchase.orderPer") }}
                        {{ productInterval(product.interval) }}
                        for {{ discountInfo.duration_in_months }} months
                        <small class="d-block text-grey-60">
                          {{ $t("components.checkout.confirmPurchase.then") }}
                          {{ product.symbol }}{{ product.price }}
                          {{ product.currency }}
                          {{
                            $t("components.checkout.confirmPurchase.orderPer")
                          }}
                          {{ productInterval(product.interval) }}
                        </small>
                      </template>
                      <template v-else>
                        {{ $t("components.checkout.confirmPurchase.first") }}
                        {{ productInterval(product.interval) }}
                        <small class="d-block text-grey-60">
                          {{ $t("components.checkout.confirmPurchase.then") }}
                          {{ product.symbol }}{{ product.price }}
                          {{ product.currency }}
                          {{
                            $t("components.checkout.confirmPurchase.orderPer")
                          }}
                          {{ productInterval(product.interval) }}
                        </small>
                      </template>
                    </template>
                  </template>
                </div>
              </list-item>
            </template>

            <template v-else>
              <list-item no-padding-top class="pb-0">
                <template slot="left">
                  <div class="confirm-purchase__left">
                    <template v-if="!isPresent(discountInfo) && showCouponForm">
                      <v-button
                        class="ml-auto"
                        round
                        ghost
                        @click="() => (showCouponForm = false)"
                      >
                        <v-icon icon="close"></v-icon>
                      </v-button>
                    </template>
                  </div>
                </template>
                <template v-if="!isPresent(discountInfo)">
                  <template v-if="showCouponForm">
                    <div class="my-1 d-flex align-items-start">
                      <v-input
                        v-model="$v.couponCode.$model"
                        class="w-100 ml-1"
                        small
                        :validator="$v.couponCode"
                        data-cy="coupon-input"
                        :placeholder="
                          $t(
                            'components.checkout.confirmPurchase.placeholderCouponForm'
                          )
                        "
                        validate-on-init
                        autofocus
                        :invalid-message="couponInvalidMessage"
                      />
                      <v-button
                        class="ml-2 flex-shrink-0"
                        primary
                        data-role="apply-coupon"
                        data-cy="apply-coupon"
                        :disabled="!couponCode"
                        :is-loading="isApplyingCoupon"
                        @click="applyCouponAsync"
                      >
                        {{ $t("components.checkout.confirmPurchase.apply") }}
                      </v-button>
                    </div>
                  </template>
                  <template v-else>
                    <v-button
                      class="d-flex align-items-center add-coupon-button"
                      primary
                      small
                      ghost
                      @click="showCouponForm = true"
                    >
                      <v-icon icon="sell" />{{
                        $t("components.checkout.confirmPurchase.applyCoupon")
                      }}
                    </v-button>
                  </template>
                </template>
              </list-item>

              <tax-info
                v-if="showTaxInfo"
                :tax-info="taxInfo"
                :is-loading="isLoadingTax"
              />
            </template>
          </list>

          <v-button
            primary
            block
            class="mt-2"
            :disabled="isLoadingTax || isApplyingCoupon"
            :is-loading="isLoadingTax"
            @click="handleConfirm"
          >
            {{
              hasTrial
                ? $t("components.checkout.confirmPurchase.continueFreeTrial")
                : $t("components.checkout.confirmPurchase.continuePayment")
            }}
          </v-button>
        </div>
      </template>
    </transition>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import { validationMixin } from "vuelidate"

import { isPresent, isBoolean } from "@/lib/utils"
import { toDate, add } from "@/lib/date-utils"
import { productInterval } from "@/lib/product-interval"
import { getReferralData } from "@/lib/rewardful"

import List from "@/components/List"
import ListItem from "@/components/ListItem"
import vInput from "@/components/forms/vInput"
import PaymentElement from "@/components/checkout/PaymentElement"
import TaxInfo from "@/components/checkout/TaxInfo"

export const COUPON_DURATIONS = {
  ONCE: "once",
  FOREVER: "forever",
  REPEATING: "repeating"
}

const DIRECTIONS = {
  FORWARD: "forward",
  BACKWARD: "backward"
}

export default {
  components: {
    List,
    ListItem,
    vInput,
    PaymentElement,
    TaxInfo
  },

  props: {
    product: { type: Object, required: true }
  },

  data() {
    return {
      couponCode: "",
      discountInfo: null,
      showCouponForm: false,
      isApplyingCoupon: false,
      couponInvalidMessage: "",

      intentType: null,
      intentClientSecret: null,

      hasConfirmed: false,
      taxInfo: null,
      noCouponTaxInfo: null,
      isLoadingTax: false,
      transitionDirection: DIRECTIONS.FORWARD,

      COUPON_DURATIONS,
      DIRECTIONS
    }
  },

  mixins: [validationMixin],

  mounted() {
    this.calculateTax()
  },

  methods: {
    isPresent,
    toDate,
    add,
    productInterval,

    ...mapActions("auth", ["signUp"]),
    ...mapActions("products", [
      "applyCoupon",
      "calculateTaxByIp",
      "fetchTaxByAddress",
      "fetchIntent"
    ]),

    handlePaymentElementError(error) {
      this.$emit("error", error)
    },

    handleUserDetailsChange({ name, address }) {
      if (this.hasEnabledTaxCollection && !this.discountZeroedPrice) {
        this.isLoadingTax = true
        this.taxInfo = null

        this.fetchTaxByAddress({
          productId: this.product.id,
          couponCode: this.couponCode,
          name,
          address
        })
          .then(({ tax }) => {
            this.taxInfo = tax

            this.isLoadingTax = false
          })
          .catch(err => {
            if (err.response?.data?.message) {
              this.$notify(err.response.data.message)
            } else {
              throw err
            }
          })
      }
    },

    calculateTax() {
      if (this.hasEnabledTaxCollection) {
        this.isLoadingTax = true
        this.taxInfo = null

        this.calculateTaxByIp({
          productId: this.product.id,
          couponCode: this.couponCode
        })
          .then(({ tax, noCouponTax }) => {
            this.taxInfo = tax
            this.noCouponTaxInfo = noCouponTax
          })
          .catch(err => {
            if (err.response?.data?.message) {
              if (
                err.response.data.reason === "customer_tax_location_invalid"
              ) {
                this.taxInfo = null
                this.noCouponTaxInfo = null
                this.isLoadingTax = false
                // allow the user to enter their address in the next step
              }
            } else {
              throw err
            }
          })
          .finally(() => {
            this.isLoadingTax = false
          })
      }
    },

    removeTax() {
      this.taxInfo = null
      this.noCouponTaxInfo = null
    },

    applyCouponAsync() {
      this.isApplyingCoupon = true
      this.applyCoupon({
        productId: this.product.id,
        couponCode: this.couponCode
      })
        .then(res => {
          this.discountInfo = res.data.discount_info

          if (this.hasEnabledTaxCollection) {
            if (this.discountZeroedPrice) {
              if (
                this.isSubscription &&
                this.discountInfo.duration === "once"
              ) {
                this.calculateTax()
              } else {
                this.removeTax()
              }
            } else {
              this.calculateTax()
            }
          }
        })
        .catch(err => {
          let message = "Coupon is not valid"
          if (err.response && err.response.data) {
            message = err.response.data.message
          }

          this.couponInvalidMessage = message
          this.discountInfo = null
        })
        .finally(() => (this.isApplyingCoupon = false))
    },

    attemptRemoveCoupon() {
      this.couponCode = ""
      this.discountInfo = null
      this.showCouponForm = false
      this.couponInvalidMessage = ""

      this.calculateTax()
    },

    handleConfirm() {
      this.transitionDirection = DIRECTIONS.FORWARD
      this.hasConfirmed = true

      if (this.stripeRequiresIntentForMount) {
        this.fetchIntent({
          productId: this.product.id,
          coupon: this.discountInfo ? this.discountInfo.code : null,
          referral: getReferralData()
        })
          .then(res => {
            const { clientSecret, intentType } = res

            this.intentType = intentType
            this.intentClientSecret = clientSecret
          })
          .catch(error => {
            console.error(error)
            this.handlePaymentElementError(error.response.data)
          })
      }
    },

    handleBack() {
      this.transitionDirection = DIRECTIONS.BACKWARD
      this.hasConfirmed = false
    }
  },

  watch: {
    couponCode(couponCode) {
      if (!couponCode) {
        this.couponInvalidMessage = ""
      }
    }
  },

  computed: {
    ...mapGetters([
      "hasEnabledTaxCollection",
      "shouldShowTax",
      "stripeRequiresIntentForMount"
    ]),

    amountInfo() {
      if (isPresent(this.taxInfo)) {
        return {
          amount: this.taxInfo.final_amount,
          amountCents: this.taxInfo.final_amount_cents,
          currency: this.taxInfo.currency,
          symbol: this.taxInfo.symbol
        }
      } else if (isPresent(this.discountInfo)) {
        return {
          amount: this.discountInfo.final_amount,
          amountCents: this.discountInfo.final_amount_cents,
          currency: this.product.currency,
          symbol: this.product.symbol
        }
      } else {
        return {
          amount: this.formattedPrice,
          amountCents: this.product.price_cents,
          currency: this.product.currency,
          symbol: this.product.symbol
        }
      }
    },

    showTaxInfo() {
      return this.hasEnabledTaxCollection && this.shouldShowTax
    },

    isSubscription() {
      return this.product.purchase_type === "recurring"
    },

    hasTrial() {
      return (
        this.isSubscription &&
        (isBoolean(this.product.can_claim_trial)
          ? this.product.can_claim_trial
          : !!this.product.trial_period_days &&
            this.product.trial_period_days > 0) // fallback
      )
    },

    formattedPrice() {
      return String(this.product.price_cents).endsWith("00")
        ? this.product.price_dollars
        : this.product.price
    },

    discountZeroedPrice() {
      return this.discountInfo?.final_amount_cents === 0
    }
  },

  validations: {
    couponCode: {}
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.confirm-purchase__left {
  width: 5rem;
  min-width: 5rem;
  max-width: 6rem;
  flex-grow: 0;
  padding-top: 0;
  display: flex;
  align-items: center;
  height: 100%;

  span {
    font-size: $font-size--small;
    color: $color-ui--grey-60;
  }
}

.confirm-purchase__card {
  height: 40px;
  box-shadow: $box-shadow--small;
  border-radius: 4px;
}

.confirm-purchase__notice {
  padding: $size--2;
  background: $color-ui--grey-10;
  font-size: $font-size--x-small;
  border-radius: 4px;
  border: 1px solid $color-ui--grey-20;
  color: $color-ui--grey-70;
  margin: $size--4;
  text-align: center;
}

.confirm-purchase__or-seperator {
  position: relative;
  text-align: center;
  padding: $size--5 0 $size--4;

  &:after {
    content: "";
    height: 1px;
    width: 100%;
    background: $color-ui--grey-20;
    position: absolute;
    left: 0;
    top: calc(#{$size--5} + 10px);
    z-index: 1;
  }

  small {
    background: $color-ui--white;
    padding: 0 $size--6;
    position: relative;
    z-index: 2;
  }
}
</style>

<style lang="scss">
@import "@/assets/styles/variables";

.confirm-purchase {
  .flex-shrink-0 {
    flex-shrink: 0;
  }
  .w-100 {
    width: 100%;
  }
  .v-list-item__center {
    flex-grow: 1;
  }

  .v-list-item {
    border-bottom: 0;

    &.--no-padding {
      padding: 0;

      .v-list-item__row {
        padding-top: 0;
      }
    }
  }

  .add-coupon-button {
    .v-icon {
      position: relative;
      top: -1px;
      margin-right: $size--1;
      font-size: $font-size--small;
      color: var(--primary-brand-color-hex);
    }
  }
}

.confirm-purchase__tax-loading {
  .v-loader {
    width: $size--5;
    height: $size--5;
  }
}
</style>
